import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  ShieldCheck,
  Upload,
  CheckCircle2,
  Circle,
  FileText,
  RefreshCw,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KYC_DOCUMENT_TYPES = [
  { key: 'company_registration', label: 'Company Registration (CIPC)', description: 'Certificate of Incorporation or CK document' },
  { key: 'tax_clearance', label: 'Tax Clearance Certificate', description: 'Valid SARS tax clearance or tax compliance status pin' },
  { key: 'director_ids', label: 'Director ID Copies', description: 'Certified copies of all directors\' identity documents' },
  { key: 'proof_of_address', label: 'Proof of Address', description: 'Utility bill or bank statement not older than 3 months' },
  { key: 'bank_confirmation', label: 'Bank Confirmation Letter', description: 'Letter from bank confirming account details' },
  { key: 'bbbee_certificate', label: 'B-BBEE Certificate', description: 'Valid Broad-Based Black Economic Empowerment certificate' },
  { key: 'financial_statements', label: 'Financial Statements', description: 'Latest audited or reviewed financial statements' },
  { key: 'shareholder_register', label: 'Shareholder Register', description: 'Register of members / shareholders' },
];

export default function ClientKYCPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [kycData, setKycData] = useState(null);
  const [uploading, setUploading] = useState(null);

  const fetchKYC = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/client/kyc`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKycData(res.data);
    } catch (error) {
      console.error('Error fetching KYC:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchKYC();
  }, [fetchKYC]);

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${API_URL}/api/client/kyc/upload?document_type=kyc_${docType}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Document uploaded successfully');
      fetchKYC();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const checklist = kycData?.checklist || {};
  const completedCount = Object.values(checklist).filter(Boolean).length;
  const totalCount = KYC_DOCUMENT_TYPES.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const getStatusBadge = (status) => {
    if (status === 'complete') return <Badge className="bg-green-100 text-green-800">Complete</Badge>;
    if (status === 'in_progress') return <Badge className="bg-amber-100 text-amber-800">In Progress</Badge>;
    return <Badge className="bg-gray-100 text-gray-800">Incomplete</Badge>;
  };

  const getDocForType = (key) => {
    return (kycData?.documents || []).find(d => d.document_type === `kyc_${key}`);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="kyc-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-kyc-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212529] font-['Manrope']">
            KYC / FICA Compliance
          </h1>
          <p className="text-[#6C757D] mt-1">
            Upload your company compliance documents
          </p>
        </div>
        <Button variant="outline" onClick={fetchKYC} data-testid="refresh-kyc">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Progress Overview */}
      <Card data-testid="kyc-progress">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-[#0D3B66]" />
              <div>
                <p className="font-medium text-[#212529]">Compliance Status</p>
                <p className="text-sm text-[#6C757D]">{completedCount} of {totalCount} documents uploaded</p>
              </div>
            </div>
            {getStatusBadge(kycData?.status)}
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#0D3B66] rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {completedCount < totalCount && (
            <div className="mt-3 flex items-center gap-2 text-sm text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              <span>Please upload remaining documents to complete your KYC verification</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Document Upload Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {KYC_DOCUMENT_TYPES.map((docType) => {
          const isUploaded = checklist[docType.key];
          const doc = getDocForType(docType.key);
          const isCurrentlyUploading = uploading === docType.key;

          return (
            <Card key={docType.key} className={isUploaded ? 'border-green-200 bg-green-50/30' : ''} data-testid={`kyc-card-${docType.key}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    {isUploaded ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                    ) : (
                      <Circle className="h-5 w-5 text-gray-300 mt-0.5" />
                    )}
                    <div>
                      <p className="font-medium text-[#212529] text-sm">{docType.label}</p>
                      <p className="text-xs text-[#6C757D] mt-0.5">{docType.description}</p>
                    </div>
                  </div>
                </div>

                {doc && (
                  <div className="flex items-center gap-2 ml-8 mb-3 text-xs text-gray-500">
                    <FileText className="h-3 w-3" />
                    <span className="truncate max-w-[200px]">{doc.filename}</span>
                    <Badge variant="outline" className="text-[10px] py-0">{doc.status === 'pending_review' ? 'Pending Review' : doc.status}</Badge>
                  </div>
                )}

                <div className="ml-8">
                  <Label htmlFor={`upload-${docType.key}`} className="cursor-pointer">
                    <div className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg hover:border-[#0D3B66] hover:bg-gray-50 transition-colors">
                      {isCurrentlyUploading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-[#0D3B66]" />
                      ) : (
                        <Upload className="h-4 w-4 text-gray-500" />
                      )}
                      <span className="text-xs text-gray-600">
                        {isCurrentlyUploading ? 'Uploading...' : isUploaded ? 'Replace document' : 'Upload document'}
                      </span>
                    </div>
                  </Label>
                  <Input
                    id={`upload-${docType.key}`}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => handleUpload(docType.key, e.target.files?.[0])}
                    disabled={isCurrentlyUploading}
                    data-testid={`upload-${docType.key}`}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
